import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getJobs, deleteJob } from '../api';
import { JOBS_TABLE_COLUMNS } from '../services/commonConstants';
import PopupMessage from './PopupMessage';

const JobsPage = () => {
  const navigate = useNavigate();
  const { isHR, isAdmin } = useAuth();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Inquiry state
  const [showInquiryPopup, setShowInquiryPopup] = useState(false);
  const [inquiryData, setInquiryData] = useState(null);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [applicationSources, setApplicationSources] = useState([]);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [jobIdFilter, setJobIdFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [jobTypeFilter, setJobTypeFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [remoteWorkFilter, setRemoteWorkFilter] = useState("");

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Form state - REMOVED: Now using separate AddJobPage
  // const [showForm, setShowForm] = useState(false);
  // const [formData, setFormData] = useState({
  //   title: "",
  //   skill: "",
  //   experience_required: "",
  //   budget: "",
  //   job_description: "",
  //   location: "",
  //   job_type: "full-time",
  //   status: "open",
  //   company: "",
  //   department: "",
  //   remote_work: false,
  //   closing_on: ""
  // });
  // const [formSubmitting, setFormSubmitting] = useState(false);

  // Edit state - REMOVED: Will implement separate EditJobPage later
  // const [editingJobId, setEditingJobId] = useState(null);
  // const [editFormData, setEditFormData] = useState({
  //   title: "",
  //   skill: "",
  //   experience_required: "",
  //   budget: "",
  //   job_description: "",
  //   location: "",
  //   job_type: "",
  //   status: "",
  //   company: "",
  //   department: "",
  //   remote_work: false,
  //   closing_on: ""
  // });

  // Fetch jobs on component mount
  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await getJobs();
      if (response.success) {
        setJobs(response.data);
      } else {
        setError(response.message);
        setShowErrorPopup(true);
      }
    } catch (err) {
      setError('Failed to fetch jobs');
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

  // Filter jobs
  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const matchesJobId = !jobIdFilter || job.job_id.toString() === jobIdFilter.toString();
      const matchesStatus = !statusFilter || job.status === statusFilter;
      const matchesJobType = !jobTypeFilter || job.job_type === jobTypeFilter;
      const matchesLocation = !locationFilter || job.location?.toLowerCase().includes(locationFilter.toLowerCase());
      const matchesCompany = !companyFilter || job.company?.toLowerCase().includes(companyFilter.toLowerCase());
      const matchesDepartment = !departmentFilter || job.department?.toLowerCase().includes(departmentFilter.toLowerCase());
      const matchesRemoteWork = !remoteWorkFilter || (remoteWorkFilter === 'yes' ? job.remote_work : !job.remote_work);

      return matchesJobId && matchesStatus && matchesJobType && matchesLocation && matchesCompany && matchesDepartment && matchesRemoteWork;
    });
  }, [jobs, jobIdFilter, statusFilter, jobTypeFilter, locationFilter, companyFilter, departmentFilter, remoteWorkFilter]);

  // Sort filtered jobs
  const sortedJobs = useMemo(() => {
    return [...filteredJobs].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredJobs, sortConfig]);

  // Get unique values for filters
  const uniqueStatuses = [...new Set(jobs.map(job => job.status).filter(Boolean))];
  const uniqueJobTypes = [...new Set(jobs.map(job => job.job_type).filter(Boolean))];
  const uniqueDepartments = [...new Set(jobs.map(job => job.department).filter(Boolean))];

  // Reset filters
  const resetFilters = () => {
    setJobIdFilter("");
    setStatusFilter("");
    setJobTypeFilter("");
    setLocationFilter("");
    setCompanyFilter("");
    setDepartmentFilter("");
    setRemoteWorkFilter("");
  };

  // Handle form input change - REMOVED: Now in AddJobPage
  // const handleFormChange = (e) => {
  //   const { name, value, type, checked } = e.target;
  //   setFormData(prev => ({
  //     ...prev,
  //     [name]: type === 'checkbox' ? checked : value
  //   }));
  // };

  // Handle inquiry button click
  const handleInquiry = async (jobId) => {
    setInquiryLoading(true);
    try {
      const { getJobInquiry, getJobApplicationSources } = await import('../api');
      const inquiryResponse = await getJobInquiry(jobId);
      if (inquiryResponse.success) {
        setInquiryData(inquiryResponse.data);
        
        // Fetch application sources
        const sourcesResponse = await getJobApplicationSources(jobId);
        if (sourcesResponse.success && sourcesResponse.data) {
          setApplicationSources(sourcesResponse.data);
        } else {
          setApplicationSources([]);
        }
        
        setShowInquiryPopup(true);
      } else {
        setError(inquiryResponse.message || 'Failed to fetch inquiry details');
        setShowErrorPopup(true);
      }
    } catch (err) {
      console.error('Error fetching inquiry:', err);
      setError('An error occurred while fetching inquiry details');
      setShowErrorPopup(true);
    } finally {
      setInquiryLoading(false);
    }
  };

  // Handle delete job
  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;

    try {
      const response = await deleteJob(jobId);
      if (response.success) {
        setSuccessMessage('Job deleted successfully');
        setShowSuccessPopup(true);
        fetchJobs(); // Refresh the list
      } else {
        setError(response.message);
        setShowErrorPopup(true);
      }
    } catch (err) {
      setError('Failed to delete job');
      setShowErrorPopup(true);
    }
  };

  // Handle form submit - REMOVED: Now in AddJobPage
  // const handleFormSubmit = async (e) => {
  //   e.preventDefault();
  //   setFormSubmitting(true);

  //   try {
  //     const response = await createJob(formData);
  //     if (response.success) {
  //       setSuccessMessage('Job created successfully');
  //       setShowSuccessPopup(true);
  //       setShowForm(false);
  //       setFormData({
  //         title: "",
  //         skill: "",
  //         experience_required: "",
  //         budget: "",
  //         job_description: "",
  //         location: "",
  //         job_type: "full-time",
  //         status: "open",
  //         company: "",
  //         department: "",
  //         remote_work: false,
  //         closing_on: ""
  //       });
  //       fetchJobs(); // Refresh the list
  //     } else {
  //       setError(response.message);
  //       setShowErrorPopup(true);
  //     }
  //   } catch (err) {
  //     setError('Failed to create job');
  //     setShowErrorPopup(true);
  //   } finally {
  //     setFormSubmitting(false);
  //   }
  // };

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  if (loading) {
    return <div className="loading">Loading jobs...</div>;
  }

  return (
    <div className="scrapper-container">
      <div className="scrapper-header">
      
        {(isHR() || isAdmin()) && (
          <button
            className="scrapper-filter-btn"
            onClick={() => navigate('/add-job')}
          >
            Add Job
          </button>
        )}
      </div>

      {/* Filter Buttons */}
      {!showFilters && (
        <div className="filter-toggle-buttons-container">
          <button 
            className="filter-toggle-show-btn"
            onClick={() => setShowFilters(true)}
          >
            Show Filters
          </button>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <>
          <div className="scrapper-filter-box">
          <div className="scrapper-filter-group">
            <label className="scrapper-label">Job ID:</label>
            <input
              type="number"
              className="scrapper-input"
              value={jobIdFilter}
              onChange={(e) => setJobIdFilter(e.target.value)}
              placeholder="Search by ID"
              min="1"
            />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Status:</label>
          <select
            className="scrapper-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {uniqueStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Job Type:</label>
          <select
            className="scrapper-input"
            value={jobTypeFilter}
            onChange={(e) => setJobTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            {uniqueJobTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Location:</label>
          <input
            type="text"
            className="scrapper-input"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            placeholder="Filter by location"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Company:</label>
          <input
            type="text"
            className="scrapper-input"
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            placeholder="Filter by company"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Department:</label>
          <select
            className="scrapper-input"
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
          >
            <option value="">All Departments</option>
            {uniqueDepartments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Remote Work:</label>
          <select
            className="scrapper-input"
            value={remoteWorkFilter}
            onChange={(e) => setRemoteWorkFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        <div className="scrapper-filter-group">
          <button
            className="scrapper-filter-btn reset-btn"
            onClick={resetFilters}
            title="Reset all filters"
          >
            Reset Filters
          </button>
        </div>
      </div>

      <div className="filter-toggle-buttons-container">
        <button 
          className="filter-toggle-hide-btn"
          onClick={() => setShowFilters(false)}
        >
          Hide Filters
        </button>
      </div>
        </>
      )}

      {/* Jobs Table */}
      <div className="scrapper-table-wrapper scrapper-table-wrapper-scroll">
        <table className="scrapper-table scrapper-table-collapse">
          <thead>
            <tr>
              {JOBS_TABLE_COLUMNS.map(column => (
                <th
                  key={column.key}
                  className={`scrapper-th ${column.isSortable ? 'scrapper-th-sortable' : ''}`}
                  onClick={column.isSortable ? () => handleSort(column.key) : undefined}
                  style={{
                    width: column.width,
                    minWidth: column.minWidth || column.width,
                    maxWidth: column.maxWidth || 'none'
                  }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedJobs.map((job, index) => (
              <tr key={job.job_id} className="scrapper-tr-hover">
                {JOBS_TABLE_COLUMNS.map(column => {
                  const cellStyle = {
                    width: column.width,
                    minWidth: column.minWidth || column.width,
                    maxWidth: column.maxWidth || 'none'
                  };

                  if (column.key === 'srNo') {
                    return <td key={column.key} className="scrapper-td" style={cellStyle}>{index + 1}</td>;
                  }
                  if (column.key === 'actions') {
                    return (
                      <td key={column.key} className="scrapper-td" style={cellStyle}>
                       
                          <div className="action-buttons">
                            <button
                              className="edit-btn scrapper-action-button"
                              onClick={() => navigate('/add-job', { state: { mode: 'edit', job } })}
                              title="Edit job"
                            >
                              ✏️
                            </button>
                            {/* <button
                              className="delete-btn scrapper-action-button"
                              onClick={() => handleDeleteJob(job.job_id)}
                              title="Delete job"
                            >
                              🗑️
                            </button> */}
                          </div>
                      </td>
                    );
                  }
                  if (column.key === 'job_id') {
                    return <td key={column.key} className="scrapper-td" style={cellStyle}>{job.job_id}</td>;
                  }
                  if (column.key === 'title') {
                    return (
                      <td key={column.key} className="scrapper-td" style={cellStyle}>
                        <button
                          type="button"
                          className="scrapper-link-button"
                          style={{ padding: 0, border: 'none', background: 'none', color: '#1a73e8', textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={() => navigate('/applied_candidate', { state: { job } })}
                        >
                          {job.title}
                        </button>
                      </td>
                    );
                  }
                  if (column.key === 'status') {
                    return (
                      <td key={column.key} className="scrapper-td" style={cellStyle}>
                        <span className={`scrapper-status-badge scrapper-status-${job.status}`}>
                          {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                        </span>
                      </td>
                    );
                  }
                  if (column.key === 'skill') {
                    return <td key={column.key} className="scrapper-td scrapper-cell-wrap" style={cellStyle}>{job.skill || '-'}</td>;
                  }
                  if (column.key === 'experience_required') {
                    return <td key={column.key} className="scrapper-td" style={cellStyle}>{job.experience_required || '-'}</td>;
                  }
                  if (column.key === 'job_type') {
                    return <td key={column.key} className="scrapper-td" style={cellStyle}>{job.job_type}</td>;
                  }
                  if (column.key === 'department') {
                    return <td key={column.key} className="scrapper-td" style={cellStyle}>{job.department || '-'}</td>;
                  }
                  if (column.key === 'job_description') {
                    return <td key={column.key} className="scrapper-td scrapper-cell-wrap" style={{ ...cellStyle, minWidth: column.minWidth }}>{job.job_description || '-'}</td>;
                  }
                  if (column.key === 'company') {
                    return <td key={column.key} className="scrapper-td" style={cellStyle}>{job.company || '-'}</td>;
                  }
                  if (column.key === 'created_by') {
                    const creatorLabel = job.creator_username || job.creator_email || job.created_by || '-';
                    return <td key={column.key} className="scrapper-td" style={cellStyle}>{creatorLabel}</td>;
                  }
                  if (column.key === 'remote_work') {
                    return (
                      <td key={column.key} className="scrapper-td" style={cellStyle}>
                        <span className="scrapper-status-badge">{job.remote_work ? 'Yes' : 'No'}</span>
                      </td>
                    );
                  }
                  if (column.key === 'location') {
                    return <td key={column.key} className="scrapper-td" style={cellStyle}>{job.location || '-'}</td>;
                  }
                  if (column.key === 'budget') {
                    return <td key={column.key} className="scrapper-td" style={cellStyle}>{job.budget ? `₹${parseInt(job.budget).toLocaleString()}` : '-'}</td>;
                  }
                  if (column.key === 'posted_on') {
                    return <td key={column.key} className="scrapper-td" style={cellStyle}>{job.posted_on ? new Date(job.posted_on).toLocaleDateString('en-IN') : '-'}</td>;
                  }
                  if (column.key === 'closing_on') {
                    return <td key={column.key} className="scrapper-td" style={cellStyle}>{job.closing_on ? new Date(job.closing_on).toLocaleDateString('en-IN') : '-'}</td>;
                  }
                  return <td key={column.key} className="scrapper-td" style={cellStyle}>{job[column.key] || '-'}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Forms moved to separate AddJobPage and EditJobPage */}
      {/* Add Job Form - REMOVED */}
      {/* Edit Job Form - REMOVED */}

      {/* Inquiry Popup */}
      {showInquiryPopup && inquiryData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '24px', color: '#333' }}>Job Inquiry Details</h2>
              <button
                onClick={() => setShowInquiryPopup(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              marginTop: '15px'
            }}>
              <tbody>
                {/* Job Details Section */}
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <td colSpan="2" style={{ padding: '12px', fontWeight: 'bold', color: '#000', fontSize: '14px', borderBottom: '2px solid #ddd' }}>
                    JOB DETAILS
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', width: '40%' }}>Job Title:</td>
                  <td style={{ padding: '12px', color: '#333' }}>{inquiryData.title}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '12px', fontWeight: 'bold', color: '#555' }}>Status:</td>
                  <td style={{ padding: '12px', color: '#333' }}>{inquiryData.status}</td>
                </tr>
                {/* <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '12px', fontWeight: 'bold', color: '#555' }}>Created At:</td>
                  <td style={{ padding: '12px', color: '#333' }}>{inquiryData.created_at ? new Date(inquiryData.created_at).toLocaleString('en-IN') : 'N/A'}</td>
                </tr> */}
                {/* Application Sources Section */}
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <td colSpan="2" style={{ padding: '12px', fontWeight: 'bold', color: '#000', fontSize: '14px', borderBottom: '2px solid #ddd', marginTop: '15px' }}>
                    JOB SOURCES
                  </td>
                </tr>
                {applicationSources && applicationSources.length > 0 ? (
                  applicationSources.map((source, index) => (
                    <React.Fragment key={index}>
                      <tr style={{ backgroundColor: '#f9f9f9', borderBottom: '1px solid #eee' }}>
                        {/* <td colSpan="2" style={{ padding: '10px 12px', fontWeight: 'bold', color: '#666', fontSize: '13px', backgroundColor: '#f0f0f0' }}>
                          Source {index + 1}: ID #{source.id || 'N/A'} — {source.source_type ? source.source_type.toUpperCase() : 'N/A'}
                        </td> */}
                      </tr>
                      {/* <tr style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>ID:</td>
                        <td style={{ padding: '12px', color: '#333', fontSize: '13px' }}>{source.id || 'N/A'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>Job ID:</td>
                        <td style={{ padding: '12px', color: '#333', fontSize: '13px' }}>{source.job_id || 'N/A'}</td>
                      </tr> */}
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>Source Type:</td>
                        <td style={{ padding: '12px', color: '#333', fontSize: '13px' }}>{source.source_type || 'N/A'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>Source Value:</td>
                        <td style={{ padding: '12px', color: '#333', fontSize: '13px' }}>
                          {source.source_value ? (
                            source.source_type === 'email' ? (
                              <a href={`mailto:${source.source_value}`} style={{ color: '#1a73e8', textDecoration: 'none' }}>{source.source_value}</a>
                            ) : source.source_type === 'whatsapp' ? (
                              <a href={`https://wa.me/${String(source.source_value).replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1a73e8', textDecoration: 'none' }}>{source.source_value}</a>
                            ) : (
                              source.source_value
                            )
                          ) : 'N/A'}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>Apply Link:</td>
                        <td style={{ padding: '12px', color: '#333', fontSize: '13px' }}>
                          {source.apply_link ? (
                            <a href={source.apply_link} target="_blank" rel="noopener noreferrer" style={{ color: '#1a73e8', textDecoration: 'none' }}>Open Link</a>
                          ) : 'N/A'}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>Credential Details:</td>
                        <td style={{ padding: '12px', color: '#333', fontSize: '13px' }}>{source.credential_details ? (typeof source.credential_details === 'string' && source.credential_details.length > 200 ? source.credential_details.slice(0,200) + '...': source.credential_details) : 'N/A'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>Instructions:</td>
                        <td style={{ padding: '12px', color: '#333', fontSize: '13px' }}>{source.instructions || 'N/A'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>Source Comment:</td>
                        <td style={{ padding: '12px', color: '#333', fontSize: '13px' }}>{source.source_comment || 'N/A'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>Created By:</td>
                        <td style={{ padding: '12px', color: '#333', fontSize: '13px' }}>{source.created_by || 'N/A'}</td>
                      </tr>
                      {/* <tr style={{ borderBottom: '1px solid #eee' }}>
                         <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>Created At:</td> 
                        <td style={{ padding: '12px', color: '#333', fontSize: '13px' }}>{source.created_at ? new Date(source.created_at).toLocaleString('en-IN') : 'N/A'}</td>
                      </tr> */}
                      {/* <tr style={{ borderBottom: '1px solid #eee' }}>
                         <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>Updated At:</td> 
                        <td style={{ padding: '12px', color: '#333', fontSize: '13px' }}>{source.updated_at ? new Date(source.updated_at).toLocaleString('en-IN') : 'N/A'}</td>
                      </tr> */}
                      {/* <tr style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>Is Active:</td>
                        <td style={{ padding: '12px', color: '#333', fontSize: '13px' }}>{(source.is_active === 1 || source.is_active === '1' || source.is_active === true) ? 'Yes' : 'No'}</td>
                      </tr> */}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2" style={{ padding: '12px', color: '#999', textAlign: 'center', fontSize: '13px' }}>
                      No application sources available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                onClick={() => setShowInquiryPopup(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#1a73e8',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popups */}
      <PopupMessage
        message={error}
        type="error"
        show={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
      />
      <PopupMessage
        message={successMessage}
        type="success"
        show={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
      />
    </div>
  );
};

export default JobsPage;